import React, { useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { TableColumn } from 'react-data-table-component'
import { useNavigate } from 'react-router-dom'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useGetDocumentTypesQuery, useUpdateDocumentTypeMutation } from '../../../store/ai/documentTypes'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'

import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'

import { DocumentType } from '../../../models/DocumentType'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'

import { UninstallDocumentTypeModal } from './UninstallDocumentTypeModal'

export const DocumentTypesTab = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const documentProcessingEnabled = hasQuota('ai.documentSummaryAgent.value')

  if (!documentProcessingEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['processDocument']} />
  }

  return <DocumentTypesTabView />
}

const DocumentTypesTabView = () => {
  const navigate = useNavigate()
  const { hasScope } = useZocksAuth()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const hasDeleteRole = hasScope('**', 'document_type:delete', ['document_type:admin'])
  const hasUpdateRole = hasScope('**', 'document_type:update', ['document_type:admin'])

  useDocumentTitle('Setup')

  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)

  const { data: documentTypes, isFetching } = useGetDocumentTypesQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, includeInactive: true },
    { refetchOnMountOrArgChange: true },
  )
  const [triggerUpdateDocumentType, { isLoading: updateIsLoading }] = useUpdateDocumentTypeMutation()

  const columns: TableColumn<DocumentType>[] = [
    {
      name: 'Document Type',
      selector: (row) => row.name,
      sortable: true,
      grow: 12,
      cell: (row) => (
        <div className='d-flex gap-3 align-items-center mw-0'>
          <div className='document-type-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0 rounded zocks-bg-color-primary-light'>
            <i className='bi bi-file-earmark-richtext zocks-text-primary' />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25 mw-0'>
            <div className='button-link-medium text-decoration-none'>{row.name}</div>
            <div className='paragraph-small text-truncate'>{row.detectionConfig.documentLabels.join(', ')}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'Tags',
      wrap: true,
      grow: 2,
      sortable: true,
      cell: (row) => <div className='d-flex gap-2 flex-wrap'>{row.systemDocumentTypeId ? <TagElement>System</TagElement> : null}</div>,
    },
    {
      grow: 0,
      name: 'Active',
      sortable: true,
      button: true,
      selector: (row) => row.isActive,
      cell: (row) => (
        <div className='d-flex align-items-center'>
          <Form.Switch checked={row.isActive} onChange={() => handleActivateDocumentType(row)} disabled={updateIsLoading || !hasUpdateRole} />
        </div>
      ),
    },
    {
      grow: 4,
      right: true,
      cell: (row) => {
        return (
          <div className='d-flex gap-3 align-items-center flex-grow-1 ps-3'>
            {!row.systemDocumentTypeId ? (
              <div className='d-flex align-items-center gap-2'>
                <Button className='p-2' variant='primaryGhost' onClick={() => handleDeleteDocumentType(row)} disabled={!hasDeleteRole}>
                  <i className='bi bi-trash' />
                </Button>
              </div>
            ) : null}
            <Button className='p-2' variant='primaryGhost' onClick={() => handleShowDocumentType(row)}>
              <i className='bi bi-gear' />
            </Button>
            <Button className='p-2' variant='primaryGhost' onClick={() => handleCopyDocumentType(row)} disabled={!hasUpdateRole}>
              <i className='bi bi-copy' />
            </Button>
          </div>
        )
      },
    },
  ]

  const _handleActivateDocumentType = (documentType: DocumentType) => {
    const { isActive, id: documentTypeId } = documentType
    triggerUpdateDocumentType({ accountId, documentTypeId, payload: { isActive: !isActive } })
  }

  const handleActivateDocumentType = tracked(
    _handleActivateDocumentType,
    {},
    {
      eventNameExtractor: (documentType: DocumentType) => {
        const { isActive } = documentType
        return isActive ? TrackEvent.DOCUMENT_TYPE_DEACTIVATED : TrackEvent.DOCUMENT_TYPE_ACTIVATED
      },
      eventDetailsExtractor: (documentType: DocumentType) => {
        const { id: documentTypeId } = documentType
        return {
          documentTypeId,
        }
      },
    },
  )

  const handleDeleteModalHide = () => {
    setSelectedDocumentType(null)
    setShowDeleteModal(false)
  }

  const handleDeleteDocumentType = (documentType: DocumentType) => {
    setSelectedDocumentType(documentType)
    setShowDeleteModal(true)
  }

  const handleShowDocumentType = (documentType: DocumentType) => {
    navigate(`/workspace/setup/document-type/document-type/details/${documentType.id}`)
  }

  const handleAddNewDocumentType = () => {
    navigate(`/workspace/setup/document-type/document-type/create`)
  }

  const handleCopyDocumentType = (documentType: DocumentType) => {
    navigate(`/workspace/setup/document-type/document-type/copy/${documentType.id}`)
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  return (
    <Container fluid>
      {showDeleteModal && selectedDocumentType ? (
        <UninstallDocumentTypeModal show={showDeleteModal} onHide={handleDeleteModalHide} documentType={selectedDocumentType} />
      ) : null}
      <Row className='mb-4'>
        <Col>
          <ScopedBlock
            missingScopeAction={MissingScopeAction.DISABLE}
            scope={{
              scope: 'document_type:create',
              superScope: ['document_type:admin'],
            }}
          >
            <Button variant='primary' onClick={handleAddNewDocumentType}>
              <i className='bi bi-plus' />
              Add new document type
            </Button>
          </ScopedBlock>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={documentTypes?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={documentTypes?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
